import { create } from 'zustand'

import { apiGetConfig } from '@src/api/config.api'
import { ChainId, ChainName, NULL_CHAINID, getChainName, getGasTokenSymbol } from '@src/configs/chainInfo'
import { isV2SupportedChain } from '@src/configs/chainInfo'
import { ConfigLoadingStatus } from '@src/enums'
import { IDeploymentConfig } from '@src/types/config.api'
import { INetwork } from '@src/types/network'
import { Address } from '@src/types/token'
import { isFeatureEnabled } from '@src/utils/environment'
import { createContextLogger } from '@src/utils/logger'

const logger = createContextLogger('useConfigStore')

export enum SwapContractType {
    CCTP = 'cctp',
    OPTIMISTIC = 'optimistic',
    ORDER_BOOK = 'order_book',
    CCTP_MESSAGE_TRANSMITTER = 'cctp_message_transmitter',
    CCTP_TOKEN_MESSENGER = 'cctp_token_messenger',
    UNISWAP_V3_QUOTER = 'uniswap_v3_quoter',
    UNISWAP_V3_ROUTER = 'uniswap_v3_router',
    UNISWAP_PERMIT2 = 'uniswap_permit2',
    CURVE_ROUTER = 'curve_router',
    ODOS_ROUTER = 'odos_router',
    LIFI_ROUTER = 'lifi_router',
    ORDER_BOOK_V2 = 'order_book_v2',
}

export type SwapContractList = Record<number, Address>

export type SwapContractsConfigType = Record<SwapContractType, SwapContractList>

type ConfigStore = {
    resolverPaused: boolean
    loading: ConfigLoadingStatus
    availableNetworks: Record<string, INetwork>
    swapContracts: SwapContractsConfigType
    restrictedChains: number[] | null
    getConfig: () => Promise<void>
    updateConfig: (updates: Partial<Pick<ConfigStore, 'restrictedChains'>>) => void
}

const initialState = {
    resolverPaused: false,
    loading: ConfigLoadingStatus.loading_not_yet_started,
    availableNetworks: {},
    restrictedChains: null,
    swapContracts: {
        [SwapContractType.CCTP]: {},
        [SwapContractType.OPTIMISTIC]: {},
        [SwapContractType.ORDER_BOOK]: {},
        [SwapContractType.CCTP_MESSAGE_TRANSMITTER]: {},
        [SwapContractType.CCTP_TOKEN_MESSENGER]: {},
        [SwapContractType.UNISWAP_V3_QUOTER]: {},
        [SwapContractType.UNISWAP_V3_ROUTER]: {},
        [SwapContractType.UNISWAP_PERMIT2]: {},
        [SwapContractType.CURVE_ROUTER]: {},
        [SwapContractType.ODOS_ROUTER]: {},
        [SwapContractType.LIFI_ROUTER]: {},
        [SwapContractType.ORDER_BOOK_V2]: {},
    },
}

const HARDCODED_TESTNET_DEPLOYMENTS: Record<string, IDeploymentConfig> = {
    sepolia: {
        abbreviation: 'SEP',
        cctp_id: NULL_CHAINID,
        chain_id: ChainId.Sepolia,
        contracts: {
            order_book: '',
            order_book_v2: '0xAf51EBE4C721909A8Aa40Cb4B1c4656b04cbc6B4',
        },
        lz_cid: NULL_CHAINID,
    },
    monadtestnet: {
        abbreviation: 'MON',
        cctp_id: NULL_CHAINID,
        chain_id: ChainId.MonadTestnet,
        contracts: {
            order_book: '',
            order_book_v2: '0x432b53c0595400f3EE94DA737CFcCA12c84fe299',
        },
        lz_cid: NULL_CHAINID,
    },
    megaethtestnet: {
        abbreviation: 'MEGA',
        cctp_id: NULL_CHAINID,
        chain_id: ChainId.MegaEthTestnet,
        contracts: {
            order_book: '',
            order_book_v2: '0x9B948b355ce7DBd00192e0257b03f84b6C65C071',
        },
        lz_cid: NULL_CHAINID,
    },
}

export const useConfigStore = create<ConfigStore>(set => ({
    ...initialState,

    getConfig: async () => {
        // logger.info('Loading config...')
        try {
            set({ loading: ConfigLoadingStatus.loading })
            let { deployments } = await apiGetConfig()

            if (isFeatureEnabled('USE_TESTNETS_ONLY')) {
                // Overwrite deployments
                deployments = HARDCODED_TESTNET_DEPLOYMENTS
            } else if (isFeatureEnabled('ENABLE_TESTNETS')) {
                // Just append testnets
                deployments = { ...deployments, ...HARDCODED_TESTNET_DEPLOYMENTS }
            }

            logger.info('Received deployments:', deployments)

            const availableNetworks: Record<string, INetwork> = {}

            const swapContracts: SwapContractsConfigType = {
                [SwapContractType.CCTP]: {},
                [SwapContractType.OPTIMISTIC]: {},
                [SwapContractType.ORDER_BOOK]: {},
                [SwapContractType.CCTP_MESSAGE_TRANSMITTER]: {},
                [SwapContractType.CCTP_TOKEN_MESSENGER]: {},
                [SwapContractType.UNISWAP_V3_QUOTER]: {},
                [SwapContractType.UNISWAP_V3_ROUTER]: {},
                [SwapContractType.UNISWAP_PERMIT2]: {},
                [SwapContractType.CURVE_ROUTER]: {},
                [SwapContractType.ODOS_ROUTER]: {},
                [SwapContractType.LIFI_ROUTER]: {},
                [SwapContractType.ORDER_BOOK_V2]: {},
            }

            Object.keys(deployments).forEach(networkKey => {
                const {
                    abbreviation,
                    chain_id,
                    cctp_id,
                    lz_cid,
                    contracts = {},
                }: {
                    abbreviation: string
                    assets: Record<string, any>
                    chain_id: number
                    cctp_id: number
                    lz_cid: number
                    contracts: Record<string, string>
                } = deployments[networkKey as ChainName] as any

                // temporarily support legacy Solana ChainId (900) while the backend is updated
                const chainId = chain_id === 900 ? ChainId.Solana : chain_id

                logger.info('[config]', chain_id, '=>', abbreviation)

                if (!isV2SupportedChain(chainId)) {
                    return
                }

                // Add network to availableNetworks
                availableNetworks[chainId] = {
                    chainId,
                    name: getChainName(networkKey as ChainName),
                    gasToken: getGasTokenSymbol(networkKey as ChainName),
                    abbreviation,
                    icon: `/assets/network/${networkKey}.png`,
                    cctp_id,
                    lz_id: lz_cid,
                }

                // Add swap contracts for supported chains
                Object.entries(contracts).forEach(([type, address]) => {
                    if (!Object.values(SwapContractType).includes(type as SwapContractType)) {
                        logger.warn(
                            `Unrecognized swap contract type received: ${type}. Recognized types are ${Object.values(
                                SwapContractType
                            )}`
                        )
                        return
                    }
                    swapContracts[type as SwapContractType][+chainId] = address as Address
                })
            })

            // Manually add Bitcoin chain
            if (isFeatureEnabled('ENABLE_FEATHER')) {
                availableNetworks[ChainId.Bitcoin] = {
                    chainId: ChainId.Bitcoin,
                    name: 'Bitcoin',
                    gasToken: 'BTC',
                    abbreviation: 'BTC',
                    icon: `/assets/network/bitcoin.png`,
                    cctp_id: NULL_CHAINID,
                    lz_id: NULL_CHAINID,
                }
            }

            set({
                availableNetworks,
                swapContracts,
                resolverPaused: false,
                loading: ConfigLoadingStatus.loaded,
            })

            logger.success('Sucessfully set the following configuration:\n', {
                availableNetworks,
                swapContracts,
                resolverPaused: false,
                loading: ConfigLoadingStatus.loaded,
            })
        } catch (err) {
            logger.error(`Error:`, err)
            set({ loading: ConfigLoadingStatus.error })
        }
    },

    updateConfig: (updates: Partial<Pick<ConfigStore, 'restrictedChains'>>) => {
        set(state => ({
            ...state,
            ...updates,
        }))
    },
}))
